import { buildProfilePageUrl, isEmptyObject, titleCaseStringArray } from '../utility-functions'
import {formatIncomingLocationData} from '../utility-functions/locationTransformer'
import { dateComparatorHelper } from '../sorters/provider-sorter'

export default function transcribe (doctorDetailResponse) {
  if (!doctorDetailResponse || isEmptyObject(doctorDetailResponse)) {
    return {}
  }

  let baseDoc = doctorDetailResponse
  let addresses = doctorDetailResponse.a || []
  let allAddresses = doctorDetailResponse.aa || []
  let schedStartDates = filterSchedStartDates(baseDoc, addresses)
  let displayAddress = getDisplayAddress(addresses, schedStartDates)

  let transformedDoc = {
    id: baseDoc.id,
    title: `${baseDoc.name}, ${baseDoc.sf}`,
    active: baseDoc.act,
    rating: baseDoc.rating,
    profilePageUrl: buildProfilePageUrl('/find-a-doctor/profile/', baseDoc.name, baseDoc.npi),
    phone: extractPrimaryPhone(displayAddress),
    photoUrl: baseDoc.photo ? baseDoc.photo : buildPhotoUrl(baseDoc),
    firstName: baseDoc.fn,
    lastName: baseDoc.ln,
    middleInitial: baseDoc.mi ? baseDoc.mi.substring(0, 1) + '.' : '',
    isActive: baseDoc.act,
    npi: baseDoc.npi,
    hasScheduleOnline: baseDoc.sched,
    medicalSuffix: baseDoc.sf,
    gender: baseDoc.g,
    experience: baseDoc.xp,
    OrganizationalUnit : baseDoc.orgUnit,
    isAcceptingPatients: baseDoc.ap === 'Yes',
    languages: baseDoc.lng,
    specialties: baseDoc.sp,
    affiliations: titleCaseStringArray(baseDoc.gp),
    hospitals: baseDoc.affiliations,
    hideContactNumbers: baseDoc.hcn,
    practices: transcribePractices(baseDoc),
    specialtyIds: baseDoc.spid,
    address: displayAddress,
    areaOfFocus: baseDoc.aoe,
    addresses,
    allAddresses,
    bio: baseDoc.bio,
    philosophy: baseDoc.poc,
    education: baseDoc.education,
    certifications: baseDoc.certifications,
    distance: displayAddress ? displayAddress.d : null,
    videoUrl: baseDoc.video,
    epicId: baseDoc.epicId,
    schedStartDates,
    locations: baseDoc.epicLoc && baseDoc.epicLoc.length > 0 ? formatIncomingLocationData(baseDoc.epicLoc) : [],
    top5aoe: baseDoc.topFiveAoe || []
  }

  let ispcpExists = transformedDoc.OrganizationalUnit.findIndex(x => x.ID === "95") !== -1
  
  if(ispcpExists){
    transformedDoc.isAcceptingPatients = baseDoc.schedStartDates && baseDoc.schedStartDates.length ? true : false
  }

  return transformedDoc
}

// This is used to make sure that, in the frontend, SchedStartDates only ever contains times
// for locations that are within the current map view (aka the Addresses property).
// We need to do this so that the First Available sort remains accurate regardless of which
// locations are in view. The provider-sorter.js logic for First Available sorts by SchedStartDates[0]
// so we also have to sort the list produced by SyncSchedStartDates by earliest to latest. 
function filterSchedStartDates (provider, addresses) {
  // If provider doesn't have any schedStartDates, return empty array
  if (!provider.schedStartDates || !provider.schedStartDates.length) return []

  // Get DepartmentIDs from Addresses property
  const validDepartmentIDs = new Set(addresses.map(addr => addr.dpt))

  // Filter SchedStartDates to only include times for locations with a valid DepartmentID
  const filteredSchedStartDates = provider.schedStartDates.filter(startDate => validDepartmentIDs.has(startDate.DepartmentId))

  // Sort SchedStartDates by earliest appointment
  filteredSchedStartDates.sort((a, b) => dateComparatorHelper(a, b))

  return filteredSchedStartDates
}

// We want the Display Address to always be the location with the earliest available appointment time
// if no location has an available appointment time, then default to the first location in Addresses
function getDisplayAddress (addresses, schedStartDates) {
  if (!addresses || !addresses.length) return null
  if (!schedStartDates || !schedStartDates.length) return addresses[0]

  const firstAvailableDepartmentId = schedStartDates[0].DepartmentId
  const matchingLocation = addresses.find(addr => addr.dpt === firstAvailableDepartmentId)

  if (!matchingLocation) return addresses[0]
  else return matchingLocation
}

export const buildPhotoUrl = doctor => {
  return doctor.img && doctor.id ? `//ohiohealth.providerconnections.net/Custom/Photos/${doctor.id}.jpg` : null
}

export const transcribePractices = doctor => {
  let practices = {}

  doctor.pr = doctor.pr || []
  doctor.pr.forEach(practice => {
    practices[practice.id] = practice.n
  })

  return practices
}

export const extractPrimaryPhone = address => {
  if (!address) { return null }
  const phones = address.cr || []

  let noFaxPhones = phones.filter(phone => phone.type !== 'Fax')
  let sortedPhones = noFaxPhones.sort((a, b) => {
    if (a.type === 'Provider Phone' && b.type !== 'Provider Phone') return -1
    if (a.type !== 'Provider Phone' && b.type === 'Provider Phone') return 1
    return 0
  })

  return sortedPhones.length > 0 ? sortedPhones[0].Number : null
}
